import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { 
  LayoutDashboard, Users, FolderKanban, Settings, LogOut, 
  FileText, Archive, UserCog, Mail, Menu, X 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/NotificationBell';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Projects', href: '/dashboard/projects', icon: FolderKanban },
    { name: 'Invoices', href: '/dashboard/invoices', icon: FileText },
  ];

  // Add Clients menu only for admin users
  if (user?.role === 'admin') {
    navigation.splice(1, 0, { name: 'Clients', href: '/dashboard/clients', icon: Users });
  }

  // Add Project Archive for ALL users (after Projects)
  const projectsIndex = navigation.findIndex(n => n.name === 'Projects');
  navigation.splice(projectsIndex + 1, 0, { name: 'Archive', href: '/dashboard/archive', icon: Archive });

  if (user?.role === 'admin') {
    navigation.push({ name: 'Mailing List', href: '/dashboard/admin/mailing-list', icon: Mail });
    navigation.push({ name: 'Admin', href: '/dashboard/admin', icon: Settings });
  }

  const isActive = (href) => location.pathname === href;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-cloud-white" data-testid="dashboard-layout">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-cool-gray sticky top-0 z-50">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-carbon-black" />
                ) : (
                  <Menu className="h-6 w-6 text-carbon-black" />
                )}
              </button>
              
              <Link to="/dashboard">
                <img
                  src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
                  alt="Fearless IT"
                  className="h-6 sm:h-8"
                  data-testid="dashboard-logo"
                />
              </Link>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Notification Bell */}
              <NotificationBell />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-2 sm:space-x-3 hover:bg-gray-50 rounded-lg p-1.5 sm:p-2 transition-colors" data-testid="user-menu-trigger">
                    <div className="text-sm text-right hidden sm:block">
                      <div className="font-semibold text-carbon-black" data-testid="user-name">{user?.first_name} {user?.last_name}</div>
                      <div className="text-slate-gray text-xs" data-testid="user-email">{user?.email}</div>
                    </div>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-fearless-blue to-electric-indigo flex items-center justify-center text-white font-bold text-sm sm:text-base">
                      {user?.first_name?.charAt(0).toUpperCase()}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {/* Show name on mobile in dropdown */}
                  <div className="sm:hidden px-2 py-1.5 border-b mb-1">
                    <div className="font-semibold text-carbon-black text-sm">{user?.first_name} {user?.last_name}</div>
                    <div className="text-slate-gray text-xs">{user?.email}</div>
                  </div>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/dashboard/settings" className="flex items-center" data-testid="settings-menu-link">
                      <UserCog className="h-4 w-4 mr-2" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                    data-testid="logout-menu-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white z-50 transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        <div className="p-4 border-b border-cool-gray flex items-center justify-between">
          <Link to="/dashboard" onClick={closeMobileMenu}>
            <img
              src="https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png"
              alt="Fearless IT"
              className="h-6"
            />
          </Link>
          <button
            onClick={closeMobileMenu}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-carbon-black" />
          </button>
        </div>
        <nav className="p-4 space-y-2 flex-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={closeMobileMenu}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  active
                    ? 'gradient-fearless text-white shadow-md'
                    : 'text-slate-gray hover:bg-gray-50'
                }`}
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Mobile User Section */}
        <div className="p-4 border-t border-cool-gray">
          <div className="flex items-center space-x-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fearless-blue to-electric-indigo flex items-center justify-center text-white font-bold">
              {user?.first_name?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-carbon-black text-sm truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-slate-gray truncate">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-1">
            <Link
              to="/dashboard/settings"
              onClick={closeMobileMenu}
              className="flex items-center space-x-3 px-4 py-2.5 rounded-lg text-slate-gray hover:bg-gray-50 transition-colors"
              data-testid="mobile-settings-link"
            >
              <UserCog className="h-5 w-5" />
              <span>Settings</span>
            </Link>
            <button
              onClick={() => {
                closeMobileMenu();
                logout();
              }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 bg-white border-r border-cool-gray min-h-[calc(100vh-73px)] p-6" data-testid="dashboard-sidebar">
          <nav className="space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${
                    active
                      ? 'gradient-fearless text-white shadow-md'
                      : 'text-slate-gray hover:bg-gray-50'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
